import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { apiBaseUrl } from '../lib/apiBaseUrl';
import { LogoMark } from '../components/ui/Logo';
import { SelectDropdown } from '../components/shared/SelectDropdown';
import { LoadError } from '../components/shared/IssuesLoadError';
import { useAppSession } from '../hooks/useAppSession';
import { toUserFacingMessage } from '@overlayqa/shared';

type Consent = { clientName: string; callbackOrigin: string; permissions: string[]; workspaces: { id: string; name: string }[] };
type Connection = { id: string; clientName: string; teamId: string; teamName: string; permissions: string[]; createdAt: string };

class ConnectionRequestError extends Error {
  constructor(message: string, readonly status: number) { super(message); }
}
const canRetryRequest = (error: unknown) => !(error instanceof ConnectionRequestError && [400, 404].includes(error.status));

export default function McpConnectPage() {
  const { getToken } = useAuth();
  const [params] = useSearchParams();
  const request = params.get('request');
  const [consent, setConsent] = useState<Consent | null>(null);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [workspace, setWorkspace] = useState('');
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(false);
  const [decision, setDecision] = useState<'allow' | 'cancel' | null>(null);
  const [error, setError] = useState('');
  const [retryable, setRetryable] = useState(true);
  const [notice, setNotice] = useState('');
  const [attempt, setAttempt] = useState(0);
  // Account setup (useAppSession -> POST /auth/bootstrap) writes the users row
  // that every /api/mcp/oauth route resolves through requireUser
  // (middleware/auth.ts). A visitor who signs up mid-connect lands here while
  // that write is in flight, and a consent read sent first is answered 401
  // "Not authenticated" (production 2026-10-16, the first Claude.ai connector
  // signup, who left 14s later). A boolean, not the session object: the session
  // refetches on window focus, and reloading the consent would wipe the chosen
  // workspace.
  const { data: session, error: sessionError, isFetching: sessionFetching, refetch: refetchSession } = useAppSession();
  const accountReady = !!session;
  const accountFailed = !accountReady && !!sessionError && !sessionFetching;

  const call = useCallback(async (path: string, method = 'GET', body?: unknown) => {
    const token = await getToken();
    if (!token) throw new Error('Please sign in again to continue.');
    const response = await fetch(`${apiBaseUrl()}/api/mcp/oauth${path}`, { method,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body) });
    const data = response.status === 204 ? null : await response.json().catch(() => null);
    if (!response.ok) throw new ConnectionRequestError(data?.error_description ?? data?.error?.message ?? 'Unable to load this connection. Try again.', response.status);
    return data;
  }, [getToken]);

  useEffect(() => {
    if (!accountReady) return;
    let active = true;
    setLoading(true);
    setError('');
    setRetryable(true);
    setConsent(null);
    setWorkspace('');
    async function load() {
      try {
        if (request) {
          const data = await call(`/consent?request=${encodeURIComponent(request)}`) as Consent;
          if (active) {
            setConsent(data);
            // A single workspace is unambiguous; multiple workspaces require
            // an explicit choice instead of silently connecting the first one.
            if (data.workspaces.length === 1) setWorkspace(data.workspaces[0].id);
          }
        } else {
          const data = await call('/connections') as { connections: Connection[] };
          if (active) setConnections(data.connections);
        }
      } catch (err) {
        if (active) {
          setError(toUserFacingMessage(err, 'Unable to load this connection.'));
          // oauthRoutes.ts GET /consent returns 400 for a consumed/expired
          // request. Reloading it cannot restart consent in the AI app.
          setRetryable(canRetryRequest(err));
        }
      }
      finally { if (active) setLoading(false); }
    }
    void load();
    return () => { active = false; };
  }, [request, call, attempt, accountReady]);

  async function decide(allow: boolean) {
    if (pending || !consent || !request) return;
    setPending(true); setDecision(allow ? 'allow' : 'cancel'); setError('');
    try {
      const result = await call('/consent', 'POST', { request, allow, ...(allow ? { teamId: workspace } : {}) }) as { redirectUrl: string };
      // The server validates the callback against registration. Check its
      // origin again here before navigating, using the displayed destination.
      if (new URL(result.redirectUrl).origin !== consent.callbackOrigin) throw new Error('The return address changed. Start again from your AI app.');
      window.location.assign(result.redirectUrl);
    } catch (err) {
      setError(toUserFacingMessage(err, 'Connection failed.'));
      // A lost response may follow a successful one-use consent write. Read
      // the request again before offering to submit the same decision twice.
      try { await call(`/consent?request=${encodeURIComponent(request)}`); }
      catch (reloadError) {
        setConsent(null);
        setRetryable(canRetryRequest(reloadError));
        setError('We could not confirm this connection. Start again from your AI app, or review your connected apps below.');
      }
      setDecision(null);
      setPending(false);
    }
  }
  async function disconnect(connection: Connection) {
    setPending(true); setError(''); setNotice('');
    try {
      await call(`/connections/${connection.id}`, 'DELETE');
      // Re-read persistence, so a successful-looking local removal cannot
      // claim the connection is revoked when the next visit still lists it.
      const result = await call('/connections') as { connections: Connection[] };
      setConnections(result.connections);
      setNotice(`${connection.clientName} was disconnected.`);
    } catch (err) { setError(toUserFacingMessage(err, 'Could not disconnect. Try again.')); }
    finally { setPending(false); }
  }

  return <div className="auth-shell flex min-h-screen items-center justify-center px-4 py-8">
    <main className="auth-shell__panel space-y-6" aria-busy={(loading && !accountFailed) || pending}>
      <LogoMark className="mx-auto h-10 w-10" />
      <div className="space-y-2 text-center">
        <h1 className="hero-title">{request ? 'Connect to OverlayQA' : 'Connected AI apps'}</h1>
        <p className="text-sm text-gray-500">{request ? 'Choose what this app can access before connecting.' : 'Disconnect an app to stop its access immediately.'}</p>
      </div>
      {accountFailed && sessionError && <LoadError resource="your account" error={sessionError} onRetry={() => refetchSession()} retrying={sessionFetching} />}
      {loading && !accountFailed && <p role="status" className="text-center text-sm text-gray-500">Loading connection details...</p>}
      {error && <div role="alert" className="auth-card__form-error">{error}</div>}
      {!loading && error && !consent && retryable && <button className="btn-secondary w-full" onClick={() => setAttempt(v => v + 1)}><span className="text-cap-trim">Try again</span></button>}
      {notice && <p role="status" className="text-sm text-gray-600">{notice}</p>}
      {!loading && consent && <>
        <div className="card-sm space-y-3">
          <p className="break-words text-sm text-gray-700"><strong>{consent.clientName}</strong> is requesting access.</p>
          <p className="break-all text-xs text-gray-500">You’ll return to {consent.callbackOrigin}</p>
          <ul className="list-disc space-y-2 pl-4 text-sm text-gray-600">{consent.permissions.map(permission => <li key={permission}>{permission}</li>)}</ul>
        </div>
        <div className="space-y-2">
          <label htmlFor="mcp-workspace" className="text-sm font-medium text-gray-700">Workspace</label>
          <SelectDropdown id="mcp-workspace" aria-label="Workspace" value={workspace} onChange={setWorkspace}
            options={consent.workspaces.map(team => ({ value: team.id, label: team.name }))} placeholder="Choose a workspace"
            emptyLabel="No workspaces available" size="lg" disabled={pending || !consent.workspaces.length} />
          <p className="text-xs text-gray-500">Your existing project permissions and account limits still apply. You can disconnect this app at any time.</p>
          {!consent.workspaces.length && <p className="text-sm text-gray-600">Join or create a workspace in OverlayQA, then start this connection again.</p>}
        </div>
        <div className="flex gap-3">
          <button className="btn-secondary flex-1" disabled={pending} onClick={() => void decide(false)}><span className="text-cap-trim">{decision === 'cancel' ? 'Cancelling...' : 'Cancel'}</span></button>
          <button className="btn-primary flex-1" disabled={pending || !workspace} onClick={() => void decide(true)}><span className="text-cap-trim">{decision === 'allow' ? 'Connecting...' : 'Allow access'}</span></button>
        </div>
      </>}
      {!loading && !request && !error && <div className="space-y-4">
        {!connections.length && <p className="text-sm text-gray-500">No AI apps are connected.</p>}
        {connections.map(connection => <section key={connection.id} className="card-sm space-y-3">
          <h2 className="break-words font-medium text-gray-800">{connection.clientName}</h2>
          <p className="break-words text-sm text-gray-600">Workspace: {connection.teamName}</p>
          <ul className="list-disc pl-4 text-sm text-gray-500 space-y-1">{connection.permissions.map(permission => <li key={permission}>{permission}</li>)}</ul>
          <button className="btn-secondary border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700" aria-label={`Disconnect ${connection.clientName}`} disabled={pending} onClick={() => void disconnect(connection)}><span className="text-cap-trim">Disconnect</span></button>
        </section>)}
      </div>}
      <p className="text-center text-xs text-gray-500"><a className="underline" href={request ? '/mcp/connect' : '/integrations'}>{request ? 'Manage connected AI apps' : 'Back to integrations'}</a></p>
    </main>
  </div>;
}
